import { eachDayOfInterval, startOfDay } from 'date-fns';
import config from '../../../../config';
import { formatPower } from '../../../../public/formatPower';
import { generateColor } from '../utils/chartColors';
import {
  buildHourlySlots,
  findClosestTimeData,
  formatDateKST,
  MAX_HOURLY_RANGE_DAYS,
} from '../utils/hourlyChartUtils';

/** /get_hourly_generation — 날짜별 10분 샘플 row 배열 조회 */
const fetchHourlyGenerationByDate = async (dateStr) => {
  const response = await fetch(`${config.backendUrl}/get_hourly_generation`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ date: dateStr }),
  });

  let result = null;
  try {
    result = await response.json();
  } catch (e) {
    console.warn(`[hourly] ${dateStr} 응답 파싱 실패:`, e);
    return [];
  }

  if (!response.ok) {
    if (response.status !== 404) {
      console.warn(`[hourly] ${dateStr} 요청 실패:`, response.status, result?.message);
    }
    return [];
  }

  if (result.status !== 'success' || !Array.isArray(result.data)) {
    console.warn(`[hourly] ${dateStr} 데이터 없음:`, result?.message);
    return [];
  }

  return result.data;
};

export const getInclusiveDateRange = (startDate, endDate) =>
  eachDayOfInterval({
    start: startOfDay(startDate),
    end: startOfDay(endDate),
  });

export const validateDateRange = (startDate, endDate) => {
  const dates = getInclusiveDateRange(startDate, endDate);
  if (dates.length > MAX_HOURLY_RANGE_DAYS) {
    throw new Error(`조회 기간은 최대 ${MAX_HOURLY_RANGE_DAYS}일까지 가능합니다.`);
  }
  if (dates.length < 1) {
    throw new Error('조회 기간을 확인해 주세요.');
  }
  return dates;
};

export const fetchHourlyGenerationRange = async (startDate, endDate) => {
  const dates = validateDateRange(startDate, endDate);
  const rawByDate = {};

  await Promise.all(
    dates.map(async (date) => {
      const dateStr = formatDateKST(date);
      rawByDate[dateStr] = await fetchHourlyGenerationByDate(dateStr);
    })
  );

  return { dates, rawByDate };
};

const formatGridPowerKw = (value) => {
  if (!value) return 'N/A';
  return (value / 1000).toFixed(3);
};

const formatAddPowerKwh = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return formatPower(value, 'Add').value;
};

const formatDetailValue = (value, divisor, decimals) => {
  if (!value && value !== 0) return 'N/A';
  return (value / divisor).toFixed(decimals);
};

const getSlotDataForSeq = (dayData, seq, hour) => {
  const filtered = dayData.filter((item) => String(item.seq) === String(seq));
  const { item } = findClosestTimeData(filtered, hour);
  const intervalAdd =
    item?.Add_power_1h != null ? Number(item.Add_power_1h) || 0 : null;

  return {
    value: intervalAdd ?? 0,
    item,
    intervalAddPower: intervalAdd,
    hasIntervalAdd: intervalAdd != null,
  };
};

/** 등록 인버터 정각 슬롯 — Add_power_1h(1시간 증분) 합산 + 툴팁용 Grid_Power 합산 */
const getSlotDataForRegistered = (dayData, hour, seqList) => {
  let sumPower = 0;
  let sumIntervalAdd = 0;
  let hasIntervalAdd = false;
  let firstItem = null;

  seqList.forEach((seq) => {
    const { item, intervalAddPower, hasIntervalAdd: hasAdd } = getSlotDataForSeq(
      dayData,
      seq,
      hour
    );
    if (item) {
      sumPower += Number(item.Grid_Power) || 0;
      if (!firstItem) firstItem = item;
    }
    if (hasAdd) {
      sumIntervalAdd += intervalAddPower;
      hasIntervalAdd = true;
    }
  });

  return {
    value: hasIntervalAdd ? sumIntervalAdd : 0,
    item: firstItem,
    gridPower: sumPower,
    intervalAddPower: hasIntervalAdd ? sumIntervalAdd : null,
    hasIntervalAdd,
  };
};

const getInverterName = (ivList, seq, index) => {
  const seqList = ivList?.seq || [];
  const nameList = ivList?.iv_name || [];
  const seqIndex = seqList.findIndex((s) => String(s) === String(seq));

  if (seqIndex >= 0 && nameList[seqIndex]) {
    return nameList[seqIndex];
  }
  if (nameList[index]) {
    return nameList[index];
  }
  return `인버터 ${seq}`;
};

export const buildHourlyDisplayData = ({ dates, rawByDate, ivList, mode }) => {
  // 정각(:00) 슬롯 — 차트 막대는 Add_power_1h(Wh), CSV/툴팁에 Grid_Power(kW) 병기
  const slots = buildHourlySlots(dates);
  const labels = slots.map((slot) => slot.label);
  const seqList = ivList?.seq || [];
  const tooltipBySlot = [];

  let datasets = [];
  let csvData = [];

  if (mode === 'total') {
    const totalData = slots.map((slot) => {
      const dayData = rawByDate[slot.date] || [];
      const { value, item, gridPower, intervalAddPower, hasIntervalAdd } = getSlotDataForRegistered(
        dayData,
        slot.hour,
        seqList
      );

      tooltipBySlot.push({
        Grid_Power: gridPower,
        Add_power_1h: hasIntervalAdd ? intervalAddPower : null,
        created_at: item?.created_at || `${slot.date} ${slot.hour}:00`,
      });

      csvData.push({
        Time: slot.label,
        Inverter: '전체 합계',
        GridPower: formatGridPowerKw(gridPower),
        AddPowerKwh: formatAddPowerKwh(hasIntervalAdd ? intervalAddPower : null),
        created_at: item?.created_at || `${slot.date} ${slot.hour}:00`,
      });

      return value;
    });

    datasets = [
      {
        label: '전체 합계',
        data: totalData,
        backgroundColor: 'rgba(255, 159, 64, 0.6)',
        borderColor: 'rgba(255, 159, 64, 1)',
        borderWidth: 1,
        maxBarThickness: 20,
      },
    ];
  } else {
    datasets = seqList.map((seq, index) => {
      const inverterName = getInverterName(ivList, seq, index);
      const color = generateColor(index, seqList.length || 1);

      const data = slots.map((slot) => {
        const dayData = rawByDate[slot.date] || [];
        const { value } = getSlotDataForSeq(dayData, seq, slot.hour);
        return value;
      });

      return {
        label: inverterName,
        data,
        backgroundColor: color.replace('hsl', 'hsla').replace(')', ', 0.6)'),
        borderColor: color,
        borderWidth: 1,
        maxBarThickness: 16,
      };
    });

    slots.forEach((slot) => {
      seqList.forEach((seq, index) => {
        const dayData = rawByDate[slot.date] || [];
        const { value, item } = getSlotDataForSeq(dayData, seq, slot.hour);
        const inverterName = getInverterName(ivList, seq, index);

        csvData.push({
          Time: slot.label,
          Inverter: inverterName,
          GridPower: formatGridPowerKw(item?.Grid_Power),
          AddPowerKwh: formatAddPowerKwh(item?.Add_power_1h ?? value),
          created_at: item?.created_at || `${slot.date} ${slot.hour}:00`,
          PvTotalVolt: formatDetailValue(item?.PvTotalVolt, 10, 1),
          PvTotalCurrent: formatDetailValue(item?.PvTotalCurrent, 10, 1),
          PvPower: formatDetailValue(item?.PvPower, 1000, 3),
          Grid_RS_Volt: formatDetailValue(item?.Grid_RS_Volt, 10, 1),
          Grid_ST_Volt: formatDetailValue(item?.Grid_ST_Volt, 10, 1),
          Grid_TR_Volt: formatDetailValue(item?.Grid_TR_Volt, 10, 1),
        });

        if (index === 0) {
          tooltipBySlot.push(item);
        }
      });
    });
  }

  return {
    chartData: { labels, datasets },
    csvData,
    tooltipBySlot,
    slots,
    mode,
  };
};

export const fetchAndBuildHourlyGeneration = async ({
  startDate,
  endDate,
  mode,
  ivList,
}) => {
  const safeIvList = {
    seq: ivList?.seq || [],
    iv_name: ivList?.iv_name || [],
    iv_capa: ivList?.iv_capa || [],
  };

  const { dates, rawByDate } = await fetchHourlyGenerationRange(startDate, endDate);
  return buildHourlyDisplayData({ dates, rawByDate, ivList: safeIvList, mode });
};
